import { env } from "@/config/env";
import type { UploadSlot } from "@/types/api";

export interface UploadProgress {
  fileName: string;
  loaded: number;
  total: number;
  /** 0..1 */
  fraction: number;
}

/** When direct blob PUT fails (often storage CORS), retry via the intake BFF. */
export interface BlobUploadProxy {
  reportId: string;
  apiBaseUrl: string;
}

function joinUrl(base: string, path: string): string {
  const b = base.endsWith("/") ? base.slice(0, -1) : base;
  const p = path.startsWith("/") ? path : `/${path}`;
  return `${b}${p}`;
}

/**
 * PUT a single file directly to its pre-signed (SAS) URL.
 *
 * Large files must never be proxied through the app server: the browser uploads
 * straight to Blob Storage using the slot's URL. We use XHR (not fetch) so we
 * can surface progress events.
 *
 * If `proxy` is set and the direct PUT fails with a network/CORS error, the
 * upload is retried once through the BFF (`PUT /api/intake/upload/{reportId}`).
 */
export function uploadFileToSlot(
  file: File,
  slot: UploadSlot,
  onProgress?: (p: UploadProgress) => void,
  proxy?: BlobUploadProxy,
): Promise<void> {
  // Mock mode issues `mock://` slots; simulate a brief upload with no network.
  if (slot.uploadUrl.startsWith("mock://")) {
    return new Promise((resolve) => {
      const total = file.size || 1;
      let loaded = 0;
      const tick = () => {
        loaded = Math.min(total, loaded + Math.ceil(total / 5));
        onProgress?.({ fileName: file.name, loaded, total, fraction: loaded / total });
        if (loaded >= total) resolve();
        else setTimeout(tick, 120);
      };
      setTimeout(tick, 120);
    });
  }

  if (proxy && env.uploadViaBff) {
    return uploadViaBffProxy(file, slot, proxy, onProgress);
  }

  return putWithXhr(file, slot, onProgress).catch(async (err) => {
    if (proxy && isNetworkUploadError(err)) {
      return uploadViaBffProxy(file, slot, proxy, onProgress);
    }
    throw err;
  });
}

function isNetworkUploadError(err: unknown): boolean {
  return err instanceof Error && err.message.startsWith("Network error uploading");
}

function putWithXhr(
  file: File,
  slot: UploadSlot,
  onProgress?: (p: UploadProgress) => void,
): Promise<void> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open("PUT", slot.uploadUrl, true);

    // Azure block-blob PUT requires this header; allow the slot to override.
    const headers: Record<string, string> = {
      "x-ms-blob-type": "BlockBlob",
      "Content-Type": file.type || "application/octet-stream",
      ...(slot.requiredHeaders ?? {}),
    };
    for (const [key, value] of Object.entries(headers)) {
      xhr.setRequestHeader(key, value);
    }

    xhr.upload.onprogress = (e) => {
      if (onProgress && e.lengthComputable) {
        onProgress({
          fileName: file.name,
          loaded: e.loaded,
          total: e.total,
          fraction: e.total > 0 ? e.loaded / e.total : 0,
        });
      }
    };

    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) resolve();
      else reject(new Error(`Upload failed for ${file.name} (HTTP ${xhr.status})`));
    };
    xhr.onerror = () =>
      reject(
        new Error(
          `Network error uploading ${file.name}. If this persists, configure Blob CORS on the storage account or ensure the intake BFF is running.`,
        ),
      );
    xhr.send(file);
  });
}

function uploadViaBffProxy(
  file: File,
  slot: UploadSlot,
  proxy: BlobUploadProxy,
  onProgress?: (p: UploadProgress) => void,
): Promise<void> {
  const url = joinUrl(
    proxy.apiBaseUrl,
    `/intake/upload/${encodeURIComponent(proxy.reportId)}`,
  );

  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open("PUT", url, true);
    xhr.setRequestHeader("X-Intake-Blob-Name", slot.blobName);
    xhr.setRequestHeader("X-Intake-File-Name", file.name);
    xhr.setRequestHeader("Content-Type", file.type || "application/octet-stream");

    xhr.upload.onprogress = (e) => {
      if (onProgress && e.lengthComputable) {
        onProgress({
          fileName: file.name,
          loaded: e.loaded,
          total: e.total,
          fraction: e.total > 0 ? e.loaded / e.total : 0,
        });
      }
    };

    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) resolve();
      else if (xhr.status === 404) {
        reject(
          new Error(
            `Upload failed for ${file.name} via BFF (HTTP 404). Restart the intake BFF (func start) so it picks up the upload proxy endpoint.`,
          ),
        );
      } else {
        let message = `Upload failed for ${file.name} via BFF (HTTP ${xhr.status})`;
        try {
          const data = JSON.parse(xhr.responseText) as { message?: string; error?: string };
          if (data.message) message = data.message;
        } catch {
          /* ignore */
        }
        reject(new Error(message));
      }
    };
    xhr.onerror = () =>
      reject(
        new Error(
          `Network error uploading ${file.name} via BFF. Check that the intake API is running at ${proxy.apiBaseUrl}.`,
        ),
      );
    xhr.send(file);
  });
}

export function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

export function fileExtension(name: string): string {
  const dot = name.lastIndexOf(".");
  return dot >= 0 ? name.slice(dot).toLowerCase() : "";
}
